import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, RequireRole, useAuth, isAdmin } from "@/lib/auth";
import BookingPage from "@/pages/BookingPage";
import LoginPage from "@/pages/LoginPage";
import ManageBookingPage from "@/pages/ManageBookingPage";
import PrivacyNoticePage from "@/pages/PrivacyNoticePage";
import LegalPage from "@/pages/LegalPage";
import { CookieBanner } from "@/components/CookieBanner";
import WhatsAppDevicePage from "@/pages/WhatsAppDevicePage";
import AdminWhatsApp from "@/pages/admin/AdminWhatsApp";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import ManagerDashboard from "@/pages/admin/ManagerDashboard";
import AdminJobs from "@/pages/admin/AdminJobs";
import AdminCantieri from "@/pages/admin/AdminCantieri";
import AdminPlanner from "@/pages/admin/AdminPlanner";
import NewCantiere from "@/pages/admin/NewCantiere";
import CantiereReport from "@/pages/admin/CantiereReport";
import AdminMap from "@/pages/admin/AdminMap";
import AdminAttendance from "@/pages/admin/AdminAttendance";
import ManualJob from "@/pages/admin/ManualJob";
import Technicians from "@/pages/admin/Technicians";
import AdminAbsences from "@/pages/admin/AdminAbsences";
import AdminSettings from "@/pages/admin/AdminSettings";
import ServiceTypes from "@/pages/admin/ServiceTypes";
import AdminMessages from "@/pages/admin/AdminMessages";
import AdminLeads from "@/pages/admin/AdminLeads";
import TechMessages from "@/pages/tech/TechMessages";
import TechLayout from "@/pages/tech/TechLayout";
import TechAgenda from "@/pages/tech/TechAgenda";
import TechJob from "@/pages/tech/TechJob";
import TechAbsences from "@/pages/tech/TechAbsences";
import TechPunch, { PUNCH_KEY } from "@/pages/tech/TechPunch";
import AdminPunches from "@/pages/admin/AdminPunches";
import AdminRoutes from "@/pages/admin/AdminRoutes";
import AdminParts from "@/pages/admin/AdminParts";

function PunchLink() {
  const k = new URLSearchParams(window.location.search).get("k");
  if (k) sessionStorage.setItem(PUNCH_KEY, k);
  return <Navigate to="/tecnico/timbra" replace />;
}

const AdminOnly = ({ children }) => <RequireRole roles={["admin"]}>{children}</RequireRole>;
const Perm = ({ keys, children }) => {
  const { user } = useAuth();
  if (isAdmin(user) || keys.some((k) => user?.permissions?.includes(k))) return children;
  return <Navigate to="/admin" replace />;
};

function DashboardHome() {
  const { user } = useAuth();
  return user?.role === "responsabile" && !isAdmin(user) ? <ManagerDashboard /> : <AdminDashboard />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<BookingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/gestisci" element={<ManageBookingPage />} />
          <Route path="/timbra" element={<PunchLink />} />
          <Route path="/informativa" element={<PrivacyNoticePage />} />
          <Route path="/privacy" element={<LegalPage doc="privacy" />} />
          <Route path="/cookie" element={<LegalPage doc="cookie" />} />
          <Route path="/termini" element={<LegalPage doc="termini" />} />
          <Route path="/whatsapp" element={<WhatsAppDevicePage />} />
          <Route path="/admin" element={<RequireRole roles={["admin", "responsabile"]}><AdminLayout /></RequireRole>}>
            <Route index element={<DashboardHome />} />
            <Route path="presenze" element={<AdminAttendance />} />
            <Route path="assenze" element={<AdminAbsences />} />
            <Route path="interventi" element={<Perm keys={["jobs_view", "jobs_edit"]}><AdminJobs /></Perm>} />
            <Route path="cantieri" element={<Perm keys={["cantieri"]}><AdminCantieri /></Perm>} />
            <Route path="pianificazione" element={<Perm keys={["cantieri"]}><AdminPlanner /></Perm>} />
            <Route path="cantieri/nuovo" element={<Perm keys={["cantieri"]}><NewCantiere /></Perm>} />
            <Route path="cantieri/:sid/rendiconto" element={<Perm keys={["cantieri"]}><CantiereReport /></Perm>} />
            <Route path="mappa" element={<AdminOnly><AdminMap /></AdminOnly>} />
            <Route path="nuovo" element={<Perm keys={["jobs_edit"]}><ManualJob /></Perm>} />
            <Route path="tecnici" element={<AdminOnly><Technicians /></AdminOnly>} />
            <Route path="messaggi" element={<AdminOnly><AdminMessages /></AdminOnly>} />
            <Route path="contatti" element={<Perm keys={["leads"]}><AdminLeads /></Perm>} />
            <Route path="timbrature" element={<Perm keys={["punches"]}><AdminPunches /></Perm>} />
            <Route path="percorsi" element={<AdminOnly><AdminRoutes /></AdminOnly>} />
            <Route path="whatsapp" element={<Perm keys={["whatsapp"]}><AdminWhatsApp /></Perm>} />
            <Route path="tipi-intervento" element={<AdminOnly><ServiceTypes /></AdminOnly>} />
            <Route path="ricambi" element={<AdminOnly><AdminParts /></AdminOnly>} />
            <Route path="impostazioni" element={<AdminOnly><AdminSettings /></AdminOnly>} />
          </Route>
          <Route path="/tecnico" element={<RequireRole roles={["tecnico", "responsabile"]}><TechLayout /></RequireRole>}>
            <Route index element={<TechAgenda />} />
            <Route path="intervento/:id" element={<TechJob />} />
            <Route path="assenze" element={<TechAbsences />} />
            <Route path="messaggi" element={<TechMessages />} />
            <Route path="timbra" element={<TechPunch />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <CookieBanner />
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </AuthProvider>
  );
}

export default App;
